import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
} from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  CalendarPlus,
  Check,
  CheckSquare,
  ChevronsUpDown,
  Handshake,
  Loader2,
  Phone,
  Plus,
  RotateCcw,
  Upload as UploadIcon,
  Video,
  X,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useConfirm } from "@/components/ui/confirm-dialog";
import {
  AttachmentZone,
  type AttachmentZoneHandle,
  type BufferedFileState,
} from "@/components/attachments/AttachmentZone";
import {
  ATTACHMENT_CAPS,
  uploadAttachment,
} from "@/api/attachments";
import { useAttachments } from "@/hooks/useAttachments";
import { useDebounce } from "@/hooks/useDebounce";
import { usePermissions } from "@/hooks/usePermissions";
import { useAuthStore } from "@/stores/auth.store";
import { useProjectStore } from "@/stores/project.store";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/formatters";
import { createTask, updateTask } from "@/api/tasks";
import { getMyProjects, listMembers } from "@/api/projects";
import { PERMISSION } from "@/lib/permissions";
import { listCompanies } from "@/api/companies";
import { listContacts } from "@/api/contacts";
import {
  buildAddToCalendarUrl,
  googleCalendarUrl,
  outlookCalendarUrl,
} from "@/lib/calendarLinks";
import {
  combineDateTimeInTz,
  splitDateTimeInTz,
  toNaiveIsoDateTime,
} from "@/lib/timezones";
import { TimezoneSelect } from "@/components/shared/TimezoneSelect";
import { useDefaultTimezone } from "@/hooks/useDefaultTimezone";
import type {
  AttachmentRead,
  Company,
  Contact,
  MyProjectRead,
  ProjectMember,
  TaskCreate,
  TaskPriority,
  TaskRead,
  TaskStatusConfig,
  TaskType,
  TaskUpdate,
} from "@/api/types";
import {
  PRIORITIES,
  PRIORITY_LABEL,
  PRIORITY_RING,
} from "./priority";
import {
  combineDateTime,
  splitDateTime,
  timeSlots,
} from "./taskFormatters";

const NO_CONTACT = "__nocontact__";

/** Outcome of the company autocomplete. "no-project" (cannot search) and
 *  "error" (searched, request failed) are deliberately distinct from an
 *  empty result set: a single boolean collapses all three into "No
 *  company found.", which reads as "this company does not exist" when
 *  the truth is that nothing was searched, or the search failed. */
type CompanySearchStatus = "idle" | "loading" | "ok" | "error" | "no-project";

/** Duration options for calls / meetings, in minutes. */
const DURATION_OPTIONS: Array<{ value: number; label: string }> = [
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 45, label: "45 minutes" },
  { value: 60, label: "1 hour" },
  { value: 90, label: "1h 30" },
  { value: 120, label: "2 hours" },
];

/** UI metadata for each task type — drives the toggle bar at the top
 *  of the dialog and the dynamic submit-button label. */
const TASK_TYPE_OPTIONS: Array<{
  value: TaskType;
  label: string;
  icon: typeof CheckSquare;
  /** Verb used in the submit button: "Create task" / "Schedule call" */
  createVerb: string;
}> = [
  { value: "task", label: "Task", icon: CheckSquare, createVerb: "Create task" },
  { value: "call", label: "Call", icon: Phone, createVerb: "Schedule call" },
  {
    value: "meeting",
    label: "Meeting",
    icon: Handshake,
    createVerb: "Schedule meeting",
  },
  {
    value: "follow_up",
    label: "Follow-up",
    icon: RotateCcw,
    createVerb: "Create follow-up",
  },
];

const schema = z.object({
  title: z
    .string()
    .min(1, "Title is required")
    .max(255, "Title is too long"),
  description: z.string().nullable(),
  due_date: z.string().nullable(),
  due_time: z.string().nullable(),
  priority: z.enum(["low", "medium", "high", "urgent"]),
  assigned_to_id: z.string().min(1, "Pick an assignee"),
  task_type: z.enum(["task", "call", "meeting", "follow_up"]),
  duration_minutes: z.number().int().nullable(),
  location: z.string().nullable(),
  contact_id: z.number().int().nullable(),
  timezone: z.string().min(1),
});

type FormValues = z.infer<typeof schema>;

/** Minimal company shape the picker needs to render its trigger —
 *  enough to show a chosen company without holding the full record. */
type CompanyLite = { id: number; name: string; domain: string | null };

interface CommonProps {
  projectSlug: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  members: ProjectMember[];
  /** Default assignee when creating. Defaults to the form's initial value. */
  defaultAssigneeId?: string;
  /** Pre-fills the new task's `status` field. Used when the dialog is
   *  opened from a kanban column's "+" button — the column hands its
   *  status key down so the task lands in the right slot on save. The
   *  page also wires this to the project's is_initial status as a
   *  fallback. Ignored on edit (the existing task's status sticks). */
  defaultStatus?: string | null;
  /** Project-configured task statuses (for resolving labels / done
   *  flags). When empty, the dialog ignores the status field — the
   *  legacy is_completed flow takes over. */
  taskStatuses?: TaskStatusConfig[];
  /** Pre-fill the due date / time fields when creating. Used by the
   *  calendar page to seed a click-on-day with a sensible default
   *  ("today at 12:00"). Ignored on edit. */
  defaultDueAt?: string;
  /** Pre-pick the task type when opening the dialog. The CompanyDetail
   *  "Schedule call" shortcut sets this to "call" so the user lands
   *  directly on the scheduled-event form. Ignored on edit. */
  defaultTaskType?: TaskType;
  /** Pre-select a contact (only meaningful for call / meeting types).
   *  Ignored on edit. */
  defaultContactId?: number | null;
  /** When provided, the company picker is locked to this company. */
  lockedCompany?: Company | CompanyLite | null;
  /** Optional curated list. When provided the picker filters it
   *  in-memory; otherwise it searches the backend as the user types. */
  companies?: Company[];
  onSaved: (task: TaskRead) => void;
}

interface CreateProps extends CommonProps {
  mode?: "create";
  task?: undefined;
}

interface EditProps extends CommonProps {
  mode: "edit";
  task: TaskRead;
}

type Props = CreateProps | EditProps;

export function TaskCreateDialog(props: Props) {
  const {
    projectSlug,
    open,
    onOpenChange,
    members,
    defaultAssigneeId,
    defaultStatus,
    taskStatuses,
    defaultDueAt,
    defaultTaskType,
    defaultContactId,
    lockedCompany,
    companies: companiesProp,
    onSaved,
  } = props;
  const isEdit = props.mode === "edit";
  const editingTask = isEdit ? (props as EditProps).task : null;
  // Default timezone for *creation* — comes from the user's stored
  // preference or the browser. Edits always seed from the saved task's
  // timezone first; this only fills in when one wasn't recorded.
  const defaultTimezone = useDefaultTimezone();

  // ─── Project picker ────────────────────────────────────────────────────
  // A new task can be filed into any project the user belongs to —
  // defaults to the one in the URL. On edit the project is fixed (you
  // don't move a task between projects); when the dialog is locked to a
  // company that company's project is implied. The control only surfaces
  // with more than one project, so single-project setups see no change.
  const [myProjects, setMyProjects] = useState<MyProjectRead[]>([]);
  // Set ONLY when the operator picks another project by hand. Everything
  // else reads `projectSlug` live, so the prop stays the single source of
  // truth. The previous shape — useState(projectSlug) plus an effect
  // re-seeding it on open — kept a second copy that is correct only as
  // long as that effect covers every way the prop can change.
  const [projectOverride, setProjectOverride] = useState<string | null>(null);
  // The slug every create-flow call routes through (company / contact
  // search, createTask, attachment uploads). Edits and company-locked
  // dialogs stay pinned to the prop slug.
  const effectiveSlug =
    !isEdit && !lockedCompany ? (projectOverride ?? projectSlug) : projectSlug;
  // Editable for a free create (not locked to a company's project);
  // read-only on edit. NOT gated on the fetch resolving — the picker
  // must mount WITH the dialog content (like the assignee Select), else
  // a Radix Select mounted later (after the async /me/projects fetch)
  // renders an invisible trigger inside the already-open Dialog.
  const showProjectPicker = !isEdit && !lockedCompany;
  const showProjectReadonly = isEdit && myProjects.length >= 1;
  const currentProjectName =
    myProjects.find((p) => p.slug === projectSlug)?.name ?? projectSlug;
  // `/me/projects` lists memberships; `/projects` (what the sidebar and
  // ProjectSync use) lists everything the user may open — a superadmin
  // sees projects there without being a member of them. Show the project
  // we are actually scoped to either way, else the Select renders its
  // placeholder and reads as "no project selected" while the form is
  // correctly scoped.
  const knownProjects = useProjectStore((s) => s.projects);
  const projectOptions = useMemo<MyProjectRead[]>(() => {
    if (!effectiveSlug) return myProjects;
    if (myProjects.some((p) => p.slug === effectiveSlug)) return myProjects;
    const known = knownProjects.find((p) => p.slug === effectiveSlug);
    return [
      {
        id: known?.id ?? -1,
        name: known?.name ?? effectiveSlug,
        slug: effectiveSlug,
      },
      ...myProjects,
    ];
  }, [myProjects, knownProjects, effectiveSlug]);

  // Fetch the user's projects whenever the picker (or the edit readonly
  // field) could show. Skipped when a company locks the project.
  useEffect(() => {
    if (!open || lockedCompany) return;
    let cancelled = false;
    getMyProjects()
      .then((ps) => !cancelled && setMyProjects(ps))
      .catch(() => !cancelled && setMyProjects([]));
    return () => {
      cancelled = true;
    };
  }, [open, lockedCompany]);

  // Drop any manual project choice each time the dialog (re)opens, so it
  // falls back to the project in the URL.
  useEffect(() => {
    if (open) setProjectOverride(null);
  }, [open, projectSlug]);

  // Company picker. We deliberately do NOT preload the project's
  // companies — M&A projects carry 20k+ — so the picker searches the
  // backend as the operator types (min 2 chars, 300 ms debounce) and
  // shows the top 20 matches. A parent may still pass a curated
  // `companies` list, in which case we filter that in-memory instead.
  const [companyQuery, setCompanyQuery] = useState("");
  const debouncedCompanyQuery = useDebounce(companyQuery, 300);
  const [fetchedCompanies, setFetchedCompanies] = useState<Company[]>([]);
  const [companiesStatus, setCompaniesStatus] =
    useState<CompanySearchStatus>("idle");
  const companies = companiesProp ?? fetchedCompanies;
  /** Server-side search mode — on unless the parent supplied a list. */
  const serverSearch = !companiesProp;

  useEffect(() => {
    if (!open || !serverSearch || lockedCompany) return;
    // No slug → no request. `/projects//companies` is never a valid URL;
    // sending it anyway spends a round-trip to render "No company found."
    // No API helper guards an empty slug, so this has to happen here.
    if (!effectiveSlug) {
      setFetchedCompanies([]);
      setCompaniesStatus("no-project");
      return;
    }
    const q = debouncedCompanyQuery.trim();
    if (q.length < 2) {
      setFetchedCompanies([]);
      setCompaniesStatus("idle");
      return;
    }
    let cancelled = false;
    setCompaniesStatus("loading");
    listCompanies(effectiveSlug, { q, page_size: 20 })
      .then((res) => {
        if (cancelled) return;
        setFetchedCompanies(res.items);
        setCompaniesStatus("ok");
      })
      .catch(() => {
        if (cancelled) return;
        setFetchedCompanies([]);
        setCompaniesStatus("error");
      });
    return () => {
      cancelled = true;
    };
  }, [open, serverSearch, lockedCompany, effectiveSlug, debouncedCompanyQuery]);

  // ─── Assignable members for the SELECTED project ───────────────────────
  // The `members` prop holds the *current* project's roster. When a free
  // create switches to another of the user's projects, the assignee list
  // must reflect THAT project — so fetch its members. Skipped on edit /
  // company-locked dialogs and when the chosen project is the prop one
  // (whose members we already have).
  const [fetchedMembers, setFetchedMembers] = useState<ProjectMember[] | null>(
    null,
  );
  const [membersLoading, setMembersLoading] = useState(false);

  useEffect(() => {
    if (!open || isEdit || lockedCompany || effectiveSlug === projectSlug) {
      setFetchedMembers(null);
      setMembersLoading(false);
      return;
    }
    let cancelled = false;
    setMembersLoading(true);
    listMembers(effectiveSlug)
      .then((ms) => !cancelled && setFetchedMembers(ms))
      .catch(() => !cancelled && setFetchedMembers([]))
      .finally(() => !cancelled && setMembersLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open, isEdit, lockedCompany, effectiveSlug, projectSlug]);

  const [companyId, setCompanyId] = useState<number | null>(null);
  // The chosen company's display info — kept separately so the trigger
  // survives a search that no longer returns it (e.g. in edit mode the
  // linked company isn't in an empty search result set).
  const [pickedCompany, setPickedCompany] = useState<CompanyLite | null>(null);
  const [companyPickerOpen, setCompanyPickerOpen] = useState(false);

  // ─── Attachments wiring ────────────────────────────────────────────────
  // Create mode buffers files in component state and flushes them
  // AFTER the task is created (so the upload endpoints have a real
  // task_id to target). Edit mode talks to the live endpoints
  // directly via useAttachments since the task_id exists already.
  const perms = usePermissions();
  const currentUser = useAuthStore((s) => s.user);
  const confirm = useConfirm();
  const attachmentZoneRef = useRef<AttachmentZoneHandle | null>(null);
  const taskCap = ATTACHMENT_CAPS.task;

  // Buffer mode (create flow) state.
  interface BufferedFile {
    /** Local UUID — keys both the React row and the per-row state. */
    id: string;
    file: File;
    state: BufferedFileState["state"];
    error?: string;
    /** Populated on success — the server's row id, kept for
     *  possible future "navigate to" affordances. */
    attachmentId?: string;
  }
  const [bufferedFiles, setBufferedFiles] = useState<BufferedFile[]>([]);
  // The upload phase drives Submit / Cancel labels + the Dialog
  // close gate. Plain four-state enum — no React Query reducer
  // needed for a single component.
  type UploadPhase =
    | "idle"
    | "creating-task"
    | "uploading-files"
    | "partial-failure"
    | "done";
  const [uploadPhase, setUploadPhase] = useState<UploadPhase>("idle");
  const [createdTaskId, setCreatedTaskId] = useState<number | null>(null);
  // `completedCount` rolls forward as each file's upload settles
  // (success OR failed). Drives the "Uploading X of Y…" label.
  const [completedCount, setCompletedCount] = useState(0);
  // Single AbortController for the whole sequential loop — abort
  // cancels the in-flight request and short-circuits the queue.
  // Stored in a ref so handlers can read it without re-renders.
  const abortControllerRef = useRef<AbortController | null>(null);

  // Modal-wide drag overlay state. Same counter pattern as Phase A's
  // page-wide overlay on CompanyDetailPage. The handlers attach to
  // DialogContent so the overlay covers the entire modal box.
  const [modalDragActive, setModalDragActive] = useState(false);
  const modalDragCounterRef = useRef(0);

  // Live mode (edit flow) — useAttachments is dormant when entityId
  // is null. We pass `editingTask?.id ?? null` so create-mode usage
  // doesn't trigger a GET against an invalid id.
  const liveAttachments = useAttachments({
    slug: projectSlug,
    entityType: "task",
    entityId: editingTask?.id ?? null,
  });

  /** Per-row delete gate in edit mode — admin OR uploader-is-me.
   *  Mirrors the company-side rule from Phase A so the rules stay
   *  symmetric across entities. */
  const canRemoveTaskAttachment = useCallback(
    (att: AttachmentRead) => {
      if (perms.canEditTasks) return true;
      if (!currentUser || !att.uploader_user_id) return false;
      return att.uploader_user_id === currentUser.id;
    },
    [perms.canEditTasks, currentUser],
  );

  /** Stable local id — crypto.randomUUID() in modern browsers,
   *  Math.random fallback for older runtimes. Both are fine for a
   *  per-component-session key. */
  const newId = useCallback((): string => {
    if (
      typeof crypto !== "undefined" &&
      typeof crypto.randomUUID === "function"
    ) {
      return crypto.randomUUID();
    }
    return `f-${Date.now().toString(36)}-${Math.random()
      .toString(36)
      .slice(2)}`;
  }, []);

  const addToBuffer = useCallback(
    (files: File[]) => {
      setBufferedFiles((prev) => [
        ...prev,
        ...files.map((f) => ({
          id: newId(),
          file: f,
          state: "idle" as const,
        })),
      ]);
    },
    [newId],
  );

  const removeFromBuffer = useCallback((index: number) => {
    setBufferedFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  /** Patch one row by stable id rather than index — uploads run
   *  sequentially so the index doesn't shift, but the parent can
   *  also remove an idle row between iterations and we don't want
   *  to overwrite the wrong record. */
  const patchBufferedFile = useCallback(
    (id: string, patch: Partial<BufferedFile>) => {
      setBufferedFiles((prev) =>
        prev.map((b) => (b.id === id ? { ...b, ...patch } : b)),
      );
    },
    [],
  );

  /** Per-row state passed to AttachmentZone. Derived from bufferedFiles
   *  with the rich shape stripped down — the component only needs
   *  id + state + error. */
  const bufferedFileStates = useMemo<BufferedFileState[]>(
    () =>
      bufferedFiles.map((b) => ({
        id: b.id,
        state: b.state,
        error: b.error,
      })),
    [bufferedFiles],
  );
  const bufferedFileObjects = useMemo<File[]>(
    () => bufferedFiles.map((b) => b.file),
    [bufferedFiles],
  );

  /** Retry one failed file in the partial-failure phase. The
   *  parent component already has a valid task id at this point —
   *  the user can only reach this state AFTER createTask resolved.
   *  Failures here re-mark the row as failed with the new error;
   *  successes flip it green and DON'T auto-close the modal
   *  (the user clicks Close explicitly). */
  const retryFile = useCallback(
    async (index: number) => {
      if (createdTaskId == null) return;
      const target = bufferedFiles[index];
      if (!target) return;
      patchBufferedFile(target.id, { state: "uploading", error: undefined });
      try {
        const created = await uploadAttachment(
          effectiveSlug,
          "task",
          createdTaskId,
          target.file,
        );
        patchBufferedFile(target.id, {
          state: "success",
          attachmentId: created.id,
        });
      } catch (err) {
        patchBufferedFile(target.id, {
          state: "failed",
          error: extractMessage(err),
        });
      }
    },
    // bufferedFiles intentionally read via the index at call time —
    // we look up by index because the AttachmentZone reports row
    // indices, not stable ids.

    [createdTaskId, effectiveSlug, bufferedFiles, patchBufferedFile],
  );

  /** Abort the in-flight upload + short-circuit the queue. The
   *  loop's catch handler turns the in-flight file into a "failed:
   *  Aborted" row; the loop then iterates the remaining files,
   *  finds the signal is aborted, and marks each of them aborted
   *  too without firing a network request. */
  const abortUploads = useCallback(async () => {
    const confirmed = await confirm({
      title: "Stop uploading?",
      description:
        "Files already uploaded will stay attached to the task. Files still uploading or queued will be marked as failed, you can retry them after.",
      confirmLabel: "Stop uploading",
      cancelLabel: "Keep going",
      variant: "warning",
    });
    if (!confirmed) return;
    abortControllerRef.current?.abort();
  }, [confirm]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: "",
      description: null,
      due_date: null,
      due_time: null,
      priority: "medium",
      assigned_to_id: defaultAssigneeId ?? "",
      task_type: "task",
      duration_minutes: 30,
      location: null,
      contact_id: null,
      // The hook resolves to the user's stored preference first, then
      // the browser's timezone — so creating a task in Argentina
      // doesn't silently default to New York.
      timezone: defaultTimezone,
    },
  });

  // Reset buffer + upload phase on every open. Stale state from a
  // previous session would otherwise bleed into the new dialog
  // (a partial-failure row from a closed dialog would re-render
  // on next open, etc.).
  useEffect(() => {
    if (!open) {
      // Make sure an in-flight loop doesn't keep running if the
      // user closes via some path that bypasses our gate (defence-
      // in-depth — the gate already blocks UI close, but a
      // programmatic open=false from the parent isn't gated).
      abortControllerRef.current?.abort();
      abortControllerRef.current = null;
      return;
    }
    setBufferedFiles([]);
    setUploadPhase("idle");
    setCreatedTaskId(null);
    setCompletedCount(0);
    setModalDragActive(false);
    modalDragCounterRef.current = 0;
  }, [open]);

  // Reset on every open or when the editing target changes.
  useEffect(() => {
    if (!open) return;
    if (editingTask) {
      // Use scheduled_at when present (call/meeting), else fall back
      // to due_at — the form unifies the two through the same date +
      // time inputs. We split the ISO *in the task's saved timezone*
      // so the displayed wall-clock matches what the creator picked.
      const start =
        editingTask.scheduled_at ?? editingTask.due_at ?? null;
      const taskTz =
        editingTask.scheduled_timezone &&
        editingTask.scheduled_timezone.length > 0
          ? editingTask.scheduled_timezone
          : defaultTimezone;
      const isCallOrMeeting =
        editingTask.task_type === "call" ||
        editingTask.task_type === "meeting";
      const split = isCallOrMeeting
        ? splitDateTimeInTz(start, taskTz)
        : splitDateTime(start);
      reset({
        title: editingTask.title,
        description: editingTask.description ?? null,
        due_date: split.date || null,
        due_time: split.time || null,
        priority: editingTask.priority,
        assigned_to_id: editingTask.assigned_to_id,
        task_type: editingTask.task_type ?? "task",
        duration_minutes: editingTask.duration_minutes ?? 30,
        location: editingTask.location ?? null,
        contact_id: editingTask.contact_id ?? null,
        timezone: taskTz,
      });
      setCompanyId(editingTask.company_id ?? null);
      setPickedCompany(
        editingTask.company_id != null
          ? {
              id: editingTask.company_id,
              name: editingTask.company_name ?? "Company",
              domain: null,
            }
          : null,
      );
      setCompanyQuery("");
    } else {
      const userTz = defaultTimezone;
      // defaultDueAt is a UTC ISO; if the dialog will land on an
      // event type, render it in the user's TZ so the time field
      // shows their wall-clock, not UTC's.
      const willBeEvent =
        defaultTaskType === "call" || defaultTaskType === "meeting";
      const seed = defaultDueAt
        ? willBeEvent
          ? splitDateTimeInTz(defaultDueAt, userTz)
          : splitDateTime(defaultDueAt)
        : null;
      reset({
        title: "",
        description: null,
        due_date: seed?.date || null,
        due_time: seed?.time || null,
        priority: "medium",
        assigned_to_id: defaultAssigneeId ?? "",
        task_type: defaultTaskType ?? "task",
        duration_minutes: 30,
        location: null,
        contact_id: null,
        timezone: userTz,
      });
      setCompanyId(lockedCompany?.id ?? null);
      setPickedCompany(null);
      setCompanyQuery("");
    }
  }, [
    open,
    editingTask,
    defaultAssigneeId,
    lockedCompany?.id,
    defaultDueAt,
    defaultTaskType,
    defaultTimezone,
    reset,
  ]);

  const priority = watch("priority");
  const assignedToId = watch("assigned_to_id");
  const dueDate = watch("due_date");
  const dueTime = watch("due_time");
  const taskType = watch("task_type");
  const durationMinutes = watch("duration_minutes");
  const location = watch("location");
  const contactId = watch("contact_id");
  const timezone = watch("timezone");

  // Whether the form should expose the scheduling block (date+time
  // required, duration, location, contact). Calls and meetings get
  // the full event shape; tasks and follow-ups can opt in to a time
  // (and the timezone select that comes with it) via "+ Add time".
  const isEvent = taskType === "call" || taskType === "meeting";
  // The time picker is forced for events and revealed for tasks /
  // follow-ups whenever `due_time` is set (either initially, by the
  // user clicking "+ Add time", or by editing a previously-timed
  // task). The timezone select piggybacks on this same predicate.
  const showTime = isEvent || (dueTime != null && dueTime.length > 0);

  // Calls don't expose a Location field — the video URL is minted by
  // the calendar provider on save. Clear any leftover value so a
  // user toggling meeting → call doesn't accidentally save a stale
  // address as the call location.
  useEffect(() => {
    if (taskType === "call") {
      setValue("location", null, { shouldDirty: false });
    }
  }, [taskType, setValue]);

  // Contacts for the picker — fetched per-company. We pull more than
  // typical (page_size 200) so the dropdown shows the whole roster
  // for large companies without paging.
  const [contacts, setContacts] = useState<Contact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);
  const effectiveCompanyId =
    lockedCompany?.id ?? companyId ?? null;
  useEffect(() => {
    if (!open || !isEvent || !effectiveCompanyId) {
      setContacts([]);
      return;
    }
    let cancelled = false;
    setContactsLoading(true);
    listContacts(effectiveSlug, {
      company_id: effectiveCompanyId,
      page_size: 200,
    })
      .then((res) => {
        if (cancelled) return;
        setContacts(res.items);
        // Auto-pick the decision-maker if the user hasn't already
        // chosen someone for this dialog session — covers both the
        // CompanyDetail "Schedule call" shortcut and the regular
        // "click company → pick call" flow.
        if (!editingTask) {
          const currentSelection = contactId;
          if (currentSelection == null && !defaultContactId) {
            const dm = res.items.find((c) => c.is_decision_maker);
            if (dm) setValue("contact_id", dm.id, { shouldDirty: false });
          } else if (defaultContactId != null && currentSelection == null) {
            setValue("contact_id", defaultContactId, {
              shouldDirty: false,
            });
          }
        }
      })
      .catch(() => !cancelled && setContacts([]))
      .finally(() => !cancelled && setContactsLoading(false));
    return () => {
      cancelled = true;
    };
    // contactId intentionally omitted — re-running on every contact
    // pick would refetch the company's roster on every click.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    open,
    isEvent,
    effectiveCompanyId,
    effectiveSlug,
    editingTask,
    defaultContactId,
    setValue,
  ]);

  // Clear the contact selection when the company changes — a contact
  // tied to one company doesn't belong on another.
  const previousCompanyRef = useMemo(
    () => ({ current: effectiveCompanyId }),
    // We only need this to track changes inside an open session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open],
  );
  useEffect(() => {
    if (!open) return;
    if (previousCompanyRef.current === effectiveCompanyId) return;
    previousCompanyRef.current = effectiveCompanyId;
    setValue("contact_id", null, { shouldDirty: false });
  }, [open, effectiveCompanyId, previousCompanyRef, setValue]);

  // Locked company wins; otherwise the company the user picked (or the
  // edit seed). Independent of the live search results so it persists.
  const selectedCompany: CompanyLite | null = lockedCompany ?? pickedCompany;

  // Selected contact (resolved from the contacts roster) — drives
  // the auto-title and the attendee email in the call/meeting link
  // generators.
  const selectedContact = useMemo(
    () =>
      contactId == null
        ? null
        : (contacts.find((c) => c.id === contactId) ?? null),
    [contacts, contactId],
  );
  // Members that can actually be assigned a task in the *selected* project.
  // Only those whose role carries tasks.edit (can complete/reopen) qualify;
  // a viewer can't work a task. Superadmins are always eligible. While
  // another project's members load we show that project's list (or empty),
  // never the stale prop roster.
  const assignableMembers = useMemo<ProjectMember[]>(() => {
    const roster =
      effectiveSlug === projectSlug ? members : (fetchedMembers ?? []);
    let list = roster.filter(
      (m) =>
        m.is_superadmin === true ||
        (m.role?.permissions?.includes(PERMISSION.TASKS_EDIT) ?? false),
    );
    // Stale / unexpected role data shouldn't make a required field
    // unfillable — fall back to the full roster if the filter empties it.
    if (list.length === 0 && roster.length > 0) list = roster;
    // Always keep the current selection visible (e.g. editing a task whose
    // assignee predates this filter, or lacks tasks.edit).
    if (assignedToId && !list.some((m) => m.id === assignedToId)) {
      const current =
        roster.find((m) => m.id === assignedToId) ??
        members.find((m) => m.id === assignedToId);
      if (current) list = [current, ...list];
    }
    return list;
  }, [effectiveSlug, projectSlug, members, fetchedMembers, assignedToId]);

  // Selected assignee — used to add the user creating the event as
  // a second attendee on the generated calendar invite.
  const selectedAssignee = useMemo(
    () =>
      assignedToId
        ? (assignableMembers.find((m) => m.id === assignedToId) ?? null)
        : null,
    [assignableMembers, assignedToId],
  );

  // Auto-title for events. Prefills the Title field with "Call with
  // X — Y" / "Meeting with X — Y" the moment a contact + company are
  // both known, but only when the title is empty or already matches a
  // previous auto-generated value (so a user-typed title is never
  // clobbered). Uses getValues so we don't re-run on every keystroke.
  useEffect(() => {
    if (isEdit) return;
    if (!isEvent) return;
    if (!selectedContact || !selectedCompany) return;
    const verb = taskType === "call" ? "Call" : "Meeting";
    const generated = `${verb} with ${selectedContact.full_name} · ${selectedCompany.name}`;
    const current = (getValues("title") ?? "").trim();
    const isAutoGenerated = /^(Call|Meeting) with .+ [·—] .+$/.test(current);
    if (current === "" || isAutoGenerated) {
      setValue("title", generated, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
  }, [
    isEdit,
    isEvent,
    taskType,
    selectedContact?.id,
    selectedContact?.full_name,
    selectedCompany?.id,
    selectedCompany?.name,
    getValues,
    setValue,
  ]);

  /** Resolve the form's current scheduled instant from the form's
   *  date / time / timezone fields. Returns null when any is missing
   *  — both link generators surface a toast and bail in that case. */
  function readScheduledRange(): { start: Date; end: Date } | null {
    const date = dueDate;
    const time = dueTime;
    if (!date || !time) {
      toast.error("Pick a date and start time first.");
      return null;
    }
    const startIso = combineDateTimeInTz(date, time, timezone);
    if (!startIso) {
      toast.error("Couldn't read the scheduled time.");
      return null;
    }
    const start = new Date(startIso);
    const dur = durationMinutes ?? 30;
    const end = new Date(start.getTime() + dur * 60_000);
    return { start, end };
  }

  /** Compose a description block from the contact's coordinates, so
   *  whoever opens the calendar invite has the contact details in
   *  context (phone, LinkedIn, etc.). */
  function buildEventDescription(): string {
    const lines = ["Scheduled via Prism CRM"];
    if (selectedContact) {
      const parts: string[] = [`Contact: ${selectedContact.full_name}`];
      if (selectedContact.email) parts[0] += ` (${selectedContact.email})`;
      lines.push(parts.join(""));
      if (selectedContact.phone) lines.push(`Phone: ${selectedContact.phone}`);
      if (selectedContact.linkedin_url) {
        lines.push(`LinkedIn: ${selectedContact.linkedin_url}`);
      }
    }
    if (selectedCompany?.name) lines.push(`Company: ${selectedCompany.name}`);
    return lines.join("\n");
  }

  /** Calls — generate a calendar invite with a Google Meet / Teams
   *  link auto-attached and the contact + assignee invited. The user
   *  clicks Save in their calendar to finalise; the meeting URL gets
   *  e-mailed out as part of the invite. */
  function generateCallLink(provider: "google" | "outlook") {
    const range = readScheduledRange();
    if (!range) return;
    const title = (watch("title") ?? "").trim() || "Call";
    const attendees = [
      selectedContact?.email,
      selectedAssignee?.email,
    ].filter((s): s is string => !!s && s.trim().length > 0);
    const url = buildAddToCalendarUrl(
      {
        title,
        start: range.start,
        end: range.end,
        timezone,
        description: buildEventDescription(),
        attendees,
        // Calls request a Meet / Teams link auto-attached.
        includeMeet: true,
      },
      provider,
    );
    window.open(url, "_blank", "noopener,noreferrer");
  }

  /** Meetings — same calendar deep-link, but with the location field
   *  carried into the event and *no* Meet/Teams attachment (it's a
   *  physical rendezvous). Participants are still invited. */
  function generateMeetingLink(provider: "google" | "outlook") {
    const range = readScheduledRange();
    if (!range) return;
    const title = (watch("title") ?? "").trim() || "Meeting";
    const attendees = [
      selectedContact?.email,
      selectedAssignee?.email,
    ].filter((s): s is string => !!s && s.trim().length > 0);
    const url = buildAddToCalendarUrl(
      {
        title,
        start: range.start,
        end: range.end,
        timezone,
        location: location ?? undefined,
        description: buildEventDescription(),
        attendees,
        includeMeet: false,
      },
      provider,
    );
    window.open(url, "_blank", "noopener,noreferrer");
  }

  async function onSubmit(values: FormValues) {
    // Same reason the company search bails out: without a slug every
    // call below targets `/projects//…`. Fail loudly here rather than
    // let the operator read a bare 404 after filling the whole form.
    if (!effectiveSlug) {
      toast.error("Select a project first.");
      return;
    }
    // When the user picks a wall-clock + timezone we send them
    // *together* without converting to UTC — `scheduled_at` and
    // `due_at` go out as naive ISO 8601 (`"2026-05-10T14:00:00"`)
    // and the `timezone` field carries the IANA zone. The backend
    // resolves the UTC instant. This keeps clients running in a
    // different zone (Argentina creating an NYC call) from
    // accidentally shipping the wrong UTC moment.
    //
    // Date-only entries (a non-event task with no time) still use
    // `combineDateTime(date, null)` — the backend gets a clean
    // end-of-day UTC ISO and there's no zone to honour.
    const isCallOrMeeting =
      values.task_type === "call" || values.task_type === "meeting";
    const hasTime = !!values.due_time;
    const eventStart = hasTime
      ? toNaiveIsoDateTime(values.due_date, values.due_time)
      : combineDateTime(values.due_date, null);
    const due_at = isCallOrMeeting
      ? eventStart
      : hasTime
        ? toNaiveIsoDateTime(values.due_date, values.due_time)
        : combineDateTime(values.due_date, null);
    try {
      if (editingTask) {
        const payload: TaskUpdate = {
          title: values.title.trim(),
          description: emptyToNull(values.description),
          company_id: lockedCompany?.id ?? companyId ?? null,
          assigned_to_id: values.assigned_to_id,
          due_at,
          priority: values.priority,
          task_type: values.task_type,
          scheduled_at: isCallOrMeeting ? eventStart : null,
          duration_minutes: isCallOrMeeting
            ? values.duration_minutes ?? 30
            : null,
          // Location is meeting-only (physical address). Calls let
          // the calendar provider mint the video URL on save.
          location:
            values.task_type === "meeting"
              ? emptyToNull(values.location)
              : null,
          contact_id: isCallOrMeeting ? values.contact_id ?? null : null,
          // TZ stored whenever a time is set, regardless of type — a
          // timed follow-up should also display correctly across
          // viewers in different zones. Wire field name is
          // `scheduled_timezone` (paired with `scheduled_at`) — the
          // backend resolves the UTC instant from the two.
          scheduled_timezone: hasTime ? values.timezone : null,
        };
        const updated = await updateTask(projectSlug, editingTask.id, payload);
        toast.success("Task updated");
        onSaved(updated);
        onOpenChange(false);
      } else {
        const payload: TaskCreate = {
          title: values.title.trim(),
          description: emptyToNull(values.description),
          company_id: lockedCompany?.id ?? companyId ?? null,
          assigned_to_id: values.assigned_to_id,
          due_at,
          priority: values.priority,
          task_type: values.task_type,
        };
        if (isCallOrMeeting) {
          payload.scheduled_at = eventStart;
          payload.duration_minutes = values.duration_minutes ?? 30;
          // Location is meeting-only (physical) — calls don't have
          // an address field; their video URL is minted by the
          // calendar provider on save.
          payload.location =
            values.task_type === "meeting"
              ? emptyToNull(values.location)
              : null;
          payload.contact_id = values.contact_id ?? null;
        }
        // TZ stored whenever a time is set — same reasoning as on
        // the edit branch above.
        if (hasTime) payload.scheduled_timezone = values.timezone;
        if (
          defaultStatus &&
          taskStatuses &&
          taskStatuses.some((s) => s.key === defaultStatus)
        ) {
          payload.status = defaultStatus;
        }

        // ─── Phase 1 — create the task ──────────────────────────────────
        // We DON'T toast here yet — the success toast fires after the
        // upload sequence finishes (so we can include attachment
        // count). Errors mid-create surface via the outer catch and
        // the modal stays open.
        setUploadPhase("creating-task");
        const created = await createTask(effectiveSlug, payload);
        setCreatedTaskId(created.id);

        // No attachments queued? Behave exactly like Phase A: toast
        // + onSaved + close. The pipeline below is skipped entirely.
        if (bufferedFiles.length === 0) {
          if (isCallOrMeeting) {
            showCalendarToast(created);
          } else {
            toast.success("Task created");
          }
          onSaved(created);
          onOpenChange(false);
          setUploadPhase("done");
          return;
        }

        // Notify the parent eagerly so its list updates regardless of
        // whether every attachment lands cleanly. Equivalent to
        // Phase A's "task IS created and DOES persist" guarantee:
        // the user sees the new row even if some uploads fail.
        onSaved(created);

        // ─── Phase 2 — sequential uploads ──────────────────────────────
        // One AbortController for the whole sequence — controller
        // .abort() cancels the in-flight request AND every queued
        // request inherits the aborted signal.
        const controller = new AbortController();
        abortControllerRef.current = controller;
        setUploadPhase("uploading-files");
        setCompletedCount(0);

        // Snapshot the queue at sequence-start. If the user removes a
        // pending row mid-sequence we still upload the snapshot —
        // their action would otherwise race the loop in confusing
        // ways. Removing only changes future-iteration intent; the
        // already-running iteration finishes its current file.
        const queue = bufferedFiles.slice();
        // Local tallies — keeping them as plain vars inside the
        // function avoids the dance of reading React state after
        // a chain of patchBufferedFile calls (state isn't readable
        // synchronously between setters, and a functional updater
        // hack can be called twice under StrictMode).
        let successCount = 0;
        let failedCount = 0;
        for (let i = 0; i < queue.length; i += 1) {
          const target = queue[i];
          // If the user aborted, mark this and every subsequent
          // file as failed/Aborted without firing a request.
          if (controller.signal.aborted) {
            patchBufferedFile(target.id, {
              state: "failed",
              error: "Aborted",
            });
            failedCount += 1;
            setCompletedCount((c) => c + 1);
            continue;
          }
          patchBufferedFile(target.id, { state: "uploading" });
          try {
            const att = await uploadAttachment(
              effectiveSlug,
              "task",
              created.id,
              target.file,
              { signal: controller.signal },
            );
            patchBufferedFile(target.id, {
              state: "success",
              attachmentId: att.id,
            });
            successCount += 1;
          } catch (err) {
            // axios.isCancel distinguishes user-initiated abort from
            // network / 4xx / 5xx errors. The string "Aborted" gives
            // the row's tooltip something meaningful even when no
            // backend error message is available.
            const isAbort = axios.isCancel(err) || controller.signal.aborted;
            patchBufferedFile(target.id, {
              state: "failed",
              error: isAbort ? "Aborted" : extractMessage(err),
            });
            failedCount += 1;
          } finally {
            setCompletedCount((c) => c + 1);
          }
        }
        abortControllerRef.current = null;

        // ─── Phase 3 — settle ─────────────────────────────────────────
        if (failedCount > 0) {
          setUploadPhase("partial-failure");
          toast.error(
            successCount > 0
              ? `${successCount} attachment${successCount === 1 ? "" : "s"} uploaded, ${queue.length - successCount} failed. Retry from the list.`
              : "Attachment uploads failed. Retry from the list, or close to keep the task without files.",
          );
          // Modal stays open. Submit button relabels to "Close".
        } else {
          if (isCallOrMeeting) {
            showCalendarToast(created);
          }
          toast.success(
            successCount === 1
              ? "Task created with 1 attachment."
              : `Task created with ${successCount} attachments.`,
          );
          onOpenChange(false);
          setUploadPhase("done");
        }
      }
    } catch (err) {
      // Errors during createTask land here — pre-upload, so the
      // pipeline never started. Reset the phase so the user can
      // adjust the form and resubmit.
      setUploadPhase("idle");
      toast.error(extractMessage(err));
    }
  }

  function showCalendarToast(task: TaskRead) {
    const gUrl = googleCalendarUrl(task);
    const oUrl = outlookCalendarUrl(task);
    const verb = task.task_type === "meeting" ? "Meeting" : "Call";
    toast.success(`${verb} scheduled ✓`, {
      duration: 6000,
      // Sonner's `action` slot only fits one button; we render two by
      // using a custom JSX description with both links. Each opens in
      // a new tab so the dialog flow isn't disrupted.
      description: (gUrl || oUrl) && (
        <div className="mt-1 flex flex-wrap gap-2">
          {gUrl && (
            <a
              href={gUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
            >
              Add to Google Calendar
            </a>
          )}
          {oUrl && (
            <a
              href={oUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center rounded-md border border-slate-200 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-700 hover:border-indigo-300 hover:bg-indigo-50 hover:text-indigo-700"
            >
              Add to Outlook
            </a>
          )}
        </div>
      ),
    });
  }

  const slots = useMemo(() => timeSlots(), []);

  // ─── Derived values for the dialog UI ─────────────────────────────────
  // Block dialog dismissal during the create + upload pipeline so a
  // misclick can't strand an in-flight task creation. Other phases are
  // freely closable.
  const isInPipeline =
    uploadPhase === "creating-task" || uploadPhase === "uploading-files";
  // Sum of buffered + already-uploaded rows so the cap toast fires at
  // the right boundary in both create (buffer) and edit (live) modes.
  const taskAttachmentCount = isEdit
    ? liveAttachments.attachments.length
    : bufferedFiles.length;
  const taskAtCap = taskAttachmentCount >= taskCap;

  /** Drop handler for the modal-wide drag overlay. Routes external
   *  drops into the AttachmentZone's existing validation pipeline via
   *  its imperative `handleFiles` ref — guarantees the same mime /
   *  size / cap rules apply regardless of where the drop lands. */
  const onModalDrop = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!perms.canEditTasks || taskAtCap) return;
      e.preventDefault();
      modalDragCounterRef.current = 0;
      setModalDragActive(false);
      const files = Array.from(e.dataTransfer?.files ?? []);
      void attachmentZoneRef.current?.handleFiles(files);
    },
    [perms.canEditTasks, taskAtCap],
  );
  const onModalDragEnter = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!perms.canEditTasks || taskAtCap) return;
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault();
      modalDragCounterRef.current += 1;
      if (modalDragCounterRef.current === 1) setModalDragActive(true);
    },
    [perms.canEditTasks, taskAtCap],
  );
  const onModalDragLeave = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!perms.canEditTasks || taskAtCap) return;
      e.preventDefault();
      modalDragCounterRef.current = Math.max(
        0,
        modalDragCounterRef.current - 1,
      );
      if (modalDragCounterRef.current === 0) setModalDragActive(false);
    },
    [perms.canEditTasks, taskAtCap],
  );
  const onModalDragOver = useCallback(
    (e: DragEvent<HTMLDivElement>) => {
      if (!perms.canEditTasks || taskAtCap) return;
      if (!e.dataTransfer?.types?.includes("Files")) return;
      e.preventDefault();
    },
    [perms.canEditTasks, taskAtCap],
  );

  /** Submit button label + disabled state, computed from the upload
   *  pipeline phase. Edit mode keeps the existing "Save" copy. */
  let submitLabel: string;
  let submitDisabled: boolean;
  let submitVariant: "primary" | "ghost" = "primary";
  let submitHandlesClose = false;
  if (isEdit) {
    submitLabel = "Save";
    submitDisabled = isSubmitting;
  } else if (uploadPhase === "creating-task") {
    submitLabel = "Creating task…";
    submitDisabled = true;
  } else if (uploadPhase === "uploading-files") {
    submitLabel = `Uploading ${completedCount} of ${bufferedFiles.length}…`;
    submitDisabled = true;
  } else if (uploadPhase === "partial-failure") {
    submitLabel = "Close";
    submitDisabled = false;
    submitHandlesClose = true;
    submitVariant = "primary";
  } else {
    submitLabel =
      TASK_TYPE_OPTIONS.find((o) => o.value === taskType)?.createVerb ??
      "Create task";
    submitDisabled = isSubmitting;
  }

  /** Cancel button label + handler, also phase-driven. */
  let cancelLabel = "Cancel";
  let cancelDisabled = false;
  let cancelHandler: () => void = () => onOpenChange(false);
  if (!isEdit) {
    if (uploadPhase === "creating-task") {
      cancelDisabled = true;
    } else if (uploadPhase === "uploading-files") {
      cancelLabel = "Abort uploads";
      cancelHandler = () => void abortUploads();
    } else if (uploadPhase === "partial-failure") {
      cancelLabel = "Close";
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        // Don't let Radix close the dialog while a create / upload
        // is in flight — the user can still bail via "Abort
        // uploads", which goes through the confirm dialog.
        if (isInPipeline) return;
        if (!isEdit && isSubmitting) return;
        onOpenChange(o);
      }}
    >
      <DialogContent
        className="flex max-h-[90vh] flex-col overflow-hidden sm:max-w-[520px]"
        onDragEnter={onModalDragEnter}
        onDragLeave={onModalDragLeave}
        onDragOver={onModalDragOver}
        onDrop={onModalDrop}
        // Belt-and-braces: Escape + outside click are already routed
        // through onOpenChange above, but Radix's escape handler runs
        // before onOpenChange — preventDefault keeps the dialog from
        // dispatching a close event we'd then have to filter.
        onEscapeKeyDown={(e) => {
          if (isInPipeline) e.preventDefault();
        }}
        onInteractOutside={(e) => {
          if (isInPipeline) e.preventDefault();
        }}
      >
        <DialogHeader className="shrink-0">
          <DialogTitle>{isEdit ? "Edit task" : "New task"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Update title, due date, assignee, or priority."
              : "Create a follow-up to keep your pipeline moving."}
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex min-h-0 flex-1 flex-col"
          noValidate
        >
          {/* Body — scrollable. The negative-mx + matching px lifts
           *  the scrollbar into the dialog's gutter so the inputs
           *  don't shift when the bar appears. */}
          <div className="-mx-1 flex-1 space-y-4 overflow-y-auto px-1 py-1">
          {/* Type selector — Task / Call / Meeting / Follow-up. Hidden
           *  on edit because changing the underlying type would
           *  invalidate the saved scheduled_at + duration without
           *  giving the user a clean way to back out. */}
          {!isEdit && (
            <Field label="Type">
              <div className="grid grid-cols-4 gap-2">
                {TASK_TYPE_OPTIONS.map((opt) => {
                  const Icon = opt.icon;
                  const active = taskType === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() =>
                        setValue("task_type", opt.value, { shouldDirty: true })
                      }
                      aria-pressed={active}
                      className={cn(
                        "flex h-9 items-center justify-center gap-1.5 rounded-lg border px-2 text-xs font-medium transition-colors",
                        active
                          ? "border-indigo-300 bg-indigo-50 text-indigo-700"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300 hover:bg-slate-50",
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" />
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </Field>
          )}

          <Field label="Title" error={errors.title?.message}>
            <Input
              {...register("title")}
              autoFocus
              maxLength={255}
              placeholder="Call CFO, send proposal…"
              className="h-10 rounded-lg border-slate-200"
            />
          </Field>

          <Field label="Description" error={errors.description?.message}>
            <Textarea
              {...register("description")}
              rows={3}
              placeholder="Optional context, links, next steps"
              className="rounded-lg border-slate-200"
            />
          </Field>

          {/* Project picker — which project the new task lands in. Sits
           *  right above Company because the company search is scoped to
           *  the selected project. Editable for a free create; read-only
           *  on edit (a task can't move between projects). */}
          {showProjectPicker ? (
            <Field label="Project">
              <Select
                value={
                  projectOptions.some((p) => p.slug === effectiveSlug)
                    ? effectiveSlug
                    : ""
                }
                onValueChange={(v) => {
                  setProjectOverride(v);
                  // A company belongs to one project — drop the picked
                  // company (and its contact) when the project changes.
                  setCompanyId(null);
                  setPickedCompany(null);
                  setCompanyQuery("");
                  setValue("contact_id", null, { shouldDirty: false });
                  // The assignee belonged to the previous project's roster —
                  // clear it so the user picks from the new project's members.
                  setValue("assigned_to_id", "", { shouldDirty: true });
                }}
              >
                <SelectTrigger
                  className="h-10 rounded-lg border-slate-200 bg-white"
                  aria-label="Project"
                >
                  <SelectValue placeholder="Select a project" />
                </SelectTrigger>
                <SelectContent>
                  {projectOptions.map((p) => (
                    <SelectItem key={p.slug} value={p.slug}>
                      {p.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          ) : showProjectReadonly ? (
            <Field label="Project">
              <div className="flex h-10 items-center rounded-lg border border-slate-200 bg-slate-50/60 px-3 text-sm text-slate-700">
                <span className="truncate">{currentProjectName}</span>
              </div>
            </Field>
          ) : null}

          <Field label="Company">
            {lockedCompany ? (
              <div className="flex h-10 items-center rounded-lg border border-slate-200 bg-slate-50/60 px-3 text-sm text-slate-700">
                <span className="truncate">{lockedCompany.name}</span>
                {lockedCompany.domain && (
                  <span className="ml-2 truncate text-xs text-slate-400">
                    {lockedCompany.domain}
                  </span>
                )}
              </div>
            ) : (
              <Popover
                open={companyPickerOpen}
                onOpenChange={setCompanyPickerOpen}
              >
                <PopoverTrigger asChild>
                  <Button
                    type="button"
                    variant="outline"
                    role="combobox"
                    aria-expanded={companyPickerOpen}
                    className="h-10 w-full justify-between rounded-lg border-slate-200 bg-white font-normal"
                  >
                    {selectedCompany ? (
                      <span className="flex min-w-0 items-baseline gap-2">
                        <span className="truncate text-slate-900">
                          {selectedCompany.name}
                        </span>
                        {selectedCompany.domain && (
                          <span className="truncate text-xs text-slate-400">
                            {selectedCompany.domain}
                          </span>
                        )}
                      </span>
                    ) : (
                      <span className="text-slate-400">
                        No company (optional)
                      </span>
                    )}
                    <span className="ml-2 flex shrink-0 items-center gap-1.5">
                      {selectedCompany && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCompanyId(null);
                            setPickedCompany(null);
                          }}
                          aria-label="Clear company"
                          className="rounded-full p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                      <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400" />
                    </span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent
                  align="start"
                  className="w-[var(--radix-popover-trigger-width)] p-0"
                >
                  <Command shouldFilter={!serverSearch}>
                    <CommandInput
                      placeholder="Search companies…"
                      value={companyQuery}
                      onValueChange={setCompanyQuery}
                    />
                    <CommandList>
                      {serverSearch && companiesStatus === "no-project" ? (
                        <div className="py-6 text-center text-sm text-slate-500">
                          Select a project first
                        </div>
                      ) : serverSearch && companyQuery.trim().length < 2 ? (
                        <div className="py-6 text-center text-sm text-slate-400">
                          Type at least 2 characters to search
                        </div>
                      ) : companiesStatus === "loading" ? (
                        <div className="flex items-center justify-center gap-2 py-6 text-sm text-slate-400">
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          Searching…
                        </div>
                      ) : companiesStatus === "error" ? (
                        <div className="py-6 text-center text-sm text-rose-600">
                          Company search failed. Try again.
                        </div>
                      ) : (
                        <>
                          <CommandEmpty>No company found.</CommandEmpty>
                          <CommandGroup>
                            {companies.map((c) => (
                              <CommandItem
                                key={c.id}
                                value={`${c.name} ${c.domain ?? ""}`}
                                onSelect={() => {
                                  setCompanyId(c.id);
                                  setPickedCompany({
                                    id: c.id,
                                    name: c.name,
                                    domain: c.domain,
                                  });
                                  setCompanyPickerOpen(false);
                                }}
                              >
                                <Check
                                  className={cn(
                                    "mr-2 h-3.5 w-3.5",
                                    companyId === c.id
                                      ? "opacity-100"
                                      : "opacity-0",
                                  )}
                                />
                                <span className="min-w-0 truncate">
                                  {c.name}
                                </span>
                                {c.domain && (
                                  <span className="ml-2 truncate text-xs text-slate-400">
                                    {c.domain}
                                  </span>
                                )}
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </>
                      )}
                    </CommandList>
                  </Command>
                </PopoverContent>
              </Popover>
            )}
          </Field>

          <Field label="Assigned to" error={errors.assigned_to_id?.message}>
            <Select
              value={assignedToId || ""}
              onValueChange={(v) =>
                setValue("assigned_to_id", v, { shouldValidate: true })
              }
            >
              <SelectTrigger className="h-10 rounded-lg border-slate-200 bg-white">
                <SelectValue placeholder="Pick a member" />
              </SelectTrigger>
              <SelectContent>
                {membersLoading && assignableMembers.length === 0 ? (
                  <div className="px-2 py-1.5 text-sm text-slate-400">
                    Loading members…
                  </div>
                ) : assignableMembers.length === 0 ? (
                  <div className="px-2 py-1.5 text-sm text-slate-400">
                    No assignable members in this project
                  </div>
                ) : (
                  assignableMembers.map((m) => (
                    <SelectItem key={m.id} value={m.id}>
                      <span className="flex items-center gap-2">
                        <Avatar className="h-5 w-5 border border-slate-200">
                          <AvatarFallback className="bg-slate-900 text-[9px] font-semibold text-white">
                            {initials(m.full_name)}
                          </AvatarFallback>
                        </Avatar>
                        <span>{m.full_name}</span>
                        <span className="text-xs text-slate-400">
                          {m.role.name}
                        </span>
                      </span>
                    </SelectItem>
                  ))
                )}
              </SelectContent>
            </Select>
          </Field>

          {/* Contact picker — events targeting a specific person can
           *  pick from the company's roster. Hidden when the dialog
           *  was opened from a company toolbar (lockedCompany) — the
           *  page-level shortcut already auto-selects the DM. */}
          {isEvent && !lockedCompany && (
            <Field label="Contact">
              <Select
                value={contactId == null ? NO_CONTACT : String(contactId)}
                onValueChange={(v) =>
                  setValue(
                    "contact_id",
                    v === NO_CONTACT ? null : Number(v),
                    { shouldDirty: true },
                  )
                }
                disabled={!effectiveCompanyId || contactsLoading}
              >
                <SelectTrigger
                  className="h-10 rounded-lg border-slate-200 bg-white"
                  aria-label="Contact"
                >
                  <SelectValue
                    placeholder={
                      !effectiveCompanyId
                        ? "Pick a company first"
                        : contactsLoading
                          ? "Loading…"
                          : "No contact"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_CONTACT}>No contact</SelectItem>
                  {contacts.map((c) => (
                    <SelectItem key={c.id} value={String(c.id)}>
                      <span className="flex items-center gap-2">
                        <span className="truncate">{c.full_name}</span>
                        {c.is_decision_maker && (
                          <span className="rounded-full bg-indigo-100 px-1.5 py-0 text-[9px] font-semibold text-indigo-700">
                            DM
                          </span>
                        )}
                        {c.title && (
                          <span className="truncate text-[11px] text-slate-400">
                            {c.title}
                          </span>
                        )}
                      </span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          )}

          {/* Scheduling block — unified across types. Date is always
           *  visible; the time picker is forced for events and
           *  optional for tasks/follow-ups (revealed via "+ Add time"
           *  and removable via the X). The timezone select appears
           *  alongside the time, regardless of type — a follow-up at
           *  09:00 Paris also benefits from cross-zone correctness. */}
          <div className="space-y-3">
            <div className={cn(showTime ? "grid grid-cols-2 gap-3" : "")}>
              <Field label={isEvent ? "Date" : "Due date"}>
                <Input
                  type="date"
                  value={dueDate ?? ""}
                  onChange={(e) =>
                    setValue("due_date", e.target.value || null, {
                      shouldDirty: true,
                    })
                  }
                  className="h-10 rounded-lg border-slate-200"
                />
              </Field>
              {showTime && (
                <Field label={isEvent ? "Start time" : "Time"}>
                  <div className="flex items-center gap-1.5">
                    <Select
                      value={dueTime ?? slots[0] ?? "09:00"}
                      onValueChange={(v) =>
                        setValue("due_time", v, { shouldDirty: true })
                      }
                    >
                      <SelectTrigger
                        className="h-10 flex-1 rounded-lg border-slate-200 bg-white"
                        aria-label={isEvent ? "Start time" : "Time"}
                      >
                        <SelectValue placeholder="Pick a time" />
                      </SelectTrigger>
                      <SelectContent>
                        {slots.map((s) => (
                          <SelectItem key={s} value={s}>
                            {s}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {!isEvent && (
                      <button
                        type="button"
                        onClick={() =>
                          setValue("due_time", null, { shouldDirty: true })
                        }
                        aria-label="Remove time"
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 text-slate-400 transition-colors hover:border-slate-300 hover:text-slate-700"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </Field>
              )}
            </div>
            {!showTime && !isEvent && (
              <button
                type="button"
                onClick={() =>
                  setValue("due_time", "09:00", { shouldDirty: true })
                }
                className="inline-flex items-center gap-1.5 text-xs font-medium text-indigo-600 transition-colors hover:text-indigo-500"
              >
                <Plus className="h-3.5 w-3.5" />
                Add time
              </button>
            )}
            {showTime && (
              <Field label="Timezone">
                <TimezoneSelect
                  value={timezone}
                  onValueChange={(v) =>
                    setValue("timezone", v, { shouldDirty: true })
                  }
                />
              </Field>
            )}
          </div>

          {/* Duration is the one event-shape field shared by both
           *  call and meeting; Location is meeting-only (a call's
           *  video URL gets minted by the calendar provider, not
           *  typed in by the user). */}
          {isEvent && (
            <div className="space-y-3">
              <div
                className={cn(
                  "grid gap-3",
                  taskType === "meeting" ? "grid-cols-2" : "grid-cols-1",
                )}
              >
                <Field label="Duration">
                  <Select
                    value={String(durationMinutes ?? 30)}
                    onValueChange={(v) =>
                      setValue("duration_minutes", Number(v), {
                        shouldDirty: true,
                      })
                    }
                  >
                    <SelectTrigger
                      className="h-10 rounded-lg border-slate-200 bg-white"
                      aria-label="Duration"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {DURATION_OPTIONS.map((d) => (
                        <SelectItem key={d.value} value={String(d.value)}>
                          {d.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                {taskType === "meeting" && (
                  <Field label="Location">
                    <Input
                      value={location ?? ""}
                      onChange={(e) =>
                        setValue("location", e.target.value || null, {
                          shouldDirty: true,
                        })
                      }
                      placeholder="Office address, meeting room…"
                      className="h-10 rounded-lg border-slate-200"
                    />
                  </Field>
                )}
              </div>

              {/* Provider buttons — different copy for call vs
               *  meeting. Both deep-link into the user's calendar
               *  pre-filled with title / time / participants /
               *  description. Calls additionally pre-toggle the
               *  Meet/Teams meeting attachment; meetings carry the
               *  physical location instead. The actual video URL
               *  (call) is minted when the user clicks Save in the
               *  calendar, which then emails the invite + link out
               *  to attendees — no OAuth needed on our side. */}
              {taskType === "call" ? (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500">
                    Create a video call and invite participants:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => generateCallLink("google")}
                    >
                      <Video className="mr-1.5 h-3.5 w-3.5" />
                      Google Meet
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => generateCallLink("outlook")}
                    >
                      <Video className="mr-1.5 h-3.5 w-3.5" />
                      Microsoft Teams
                    </Button>
                  </div>
                  <p className="text-[11px] text-slate-400">
                    Opens your calendar to create the event. A video link
                    will be generated automatically when you save it.
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  <p className="text-xs text-slate-500">
                    Block the slot in your calendar:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => generateMeetingLink("google")}
                    >
                      <CalendarPlus className="mr-1.5 h-3.5 w-3.5" />
                      Add to Google Calendar
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => generateMeetingLink("outlook")}
                    >
                      <CalendarPlus className="mr-1.5 h-3.5 w-3.5" />
                      Add to Outlook
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}

          <Field label="Priority">
            <div className="grid grid-cols-4 gap-2">
              {PRIORITIES.map((p) => (
                <PriorityButton
                  key={p}
                  priority={p}
                  active={priority === p}
                  onClick={() =>
                    setValue("priority", p, { shouldDirty: true })
                  }
                />
              ))}
            </div>
          </Field>

          {/* Attachments — buffer queue in create flow, live list in
           *  edit. Hidden entirely for Viewers in create mode (they
           *  shouldn't see the buffer affordance at all); in edit
           *  mode the live list is read-only because canUpload +
           *  canRemove both fall through to false. */}
          {isEdit && editingTask ? (
            <AttachmentZone
              ref={attachmentZoneRef}
              mode="live"
              variant="compact"
              attachments={liveAttachments.attachments}
              isLoading={
                liveAttachments.isLoading && !liveAttachments.loaded
              }
              cap={liveAttachments.cap}
              canUpload={perms.canEditTasks}
              canRemove={canRemoveTaskAttachment}
              onUpload={liveAttachments.upload}
              onRemove={liveAttachments.remove}
              onDownload={liveAttachments.download}
            />
          ) : (
            perms.canEditTasks && (
              <AttachmentZone
                ref={attachmentZoneRef}
                mode="buffer"
                variant="compact"
                cap={taskCap}
                bufferedFiles={bufferedFileObjects}
                bufferedFileStates={bufferedFileStates}
                onBufferAdd={addToBuffer}
                onBufferRemove={removeFromBuffer}
                onRetry={retryFile}
                // Always allow upload when the user has the perm;
                // the cap toast comes from validateAttachmentFiles.
                canUpload={perms.canEditTasks}
              />
            )
          )}
          </div>

          <DialogFooter className="shrink-0 border-t border-slate-900/[0.06] pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={cancelHandler}
              disabled={cancelDisabled}
            >
              {cancelLabel}
            </Button>
            <Button
              type={submitHandlesClose ? "button" : "submit"}
              variant={submitVariant}
              disabled={submitDisabled}
              onClick={
                // In partial-failure, the Submit button is repurposed
                // as a Close affordance — it doesn't re-submit the
                // form (the task is already saved server-side).
                submitHandlesClose
                  ? () => onOpenChange(false)
                  : undefined
              }
            >
              {(isSubmitting ||
                uploadPhase === "creating-task" ||
                uploadPhase === "uploading-files") && (
                <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              )}
              {submitLabel}
            </Button>
          </DialogFooter>
        </form>

        {/* Modal-wide drop overlay. Lives inside DialogContent (fixed
         *  position) so absolute inset-0 fills the modal box, NOT
         *  the page. pointer-events-none keeps the overlay purely
         *  visual — drop events bubble up to DialogContent's
         *  onDrop, which routes via attachmentZoneRef. */}
        {modalDragActive && (
          <div
            className="pointer-events-none absolute inset-0 z-50 flex items-center justify-center rounded-lg bg-indigo-500/15 backdrop-blur-[1px]"
            aria-hidden
          >
            <div className="flex flex-col items-center gap-2 rounded-plane border-2 border-dashed border-indigo-400 bg-white/90 px-6 py-5 text-indigo-700 shadow-elevated">
              <UploadIcon className="h-5 w-5" />
              <span className="text-sm font-medium">
                Drop to attach to this task
              </span>
              <span className="text-xs text-indigo-500">
                PDF, DOCX, XLSX, PPTX, JPG, PNG · up to 25 MB
              </span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PriorityButton({
  priority,
  active,
  onClick,
}: {
  priority: TaskPriority;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex items-center justify-center rounded-lg border px-2 py-2 text-xs font-medium transition-colors",
        active
          ? PRIORITY_RING[priority]
          : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50",
      )}
      aria-pressed={active}
    >
      {PRIORITY_LABEL[priority]}
    </button>
  );
}

function Field({
  label,
  error,
  children,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
        {label}
      </label>
      {children}
      {error && <p className="text-xs text-rose-600">{error}</p>}
    </div>
  );
}

function emptyToNull(v: string | null | undefined): string | null {
  if (v === null || v === undefined) return null;
  return v.trim() === "" ? null : v.trim();
}

function extractMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    const detail = (err.response?.data as { detail?: unknown } | undefined)
      ?.detail;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0] as { msg?: string };
      if (first?.msg) return first.msg;
    }
    if (err.code === "ERR_NETWORK") return "Cannot reach the server.";
  }
  return "Couldn't save the task.";
}
